import { Auth } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { useBoolean } from 'react-use';
import { LoginForm, LoginFormData } from 'domains/auth/LoginForm';
import { useFlashMessages } from 'domains/flash-messages';
import { useCognitoUserContext } from 'domains/auth/AWSCognitoWrapper/context';
import { CognitoUser } from 'domains/auth/AWSCognitoWrapper/types';
import { PageContainer } from '@expressable/syntax-ds';

export const Login: React.FC = () => {
  const cognitoCtx = useCognitoUserContext();
  if (cognitoCtx.isLoggedIn) {
    throw new Error(`<Login/> component shouldn't render if there's an active session`);
  }

  const { onLogin } = cognitoCtx;

  const [isLoading, setIsLoading] = useBoolean(false);

  const { showFlash, clearFlash } = useFlashMessages();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const login = async ({ email, password }: LoginFormData) => {
    setIsLoading(true);
    clearFlash();

    try {
      const user = await Auth.signIn(email, password.trim());
      // https://docs.amplify.aws/lib/auth/manageusers/q/platform/js/#complete-new-password
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        navigate({
          pathname: '/invitation',
          search: `?${createSearchParams({
            email,
            code: password,
          })}`,
        });
      } else {
        onLogin(user as CognitoUser);
      }
    } catch {
      showFlash([
        {
          variant: 'error',
          message: t('login.form.error'),
          key: 'form-feedback',
        },
      ]);
    }

    setIsLoading(false);
  };

  return (
    <>
      <PageContainer variant="small-form" className="mt-[0px] mb-[0px]">
        <LoginForm onSubmit={login} loading={isLoading} />
      </PageContainer>
    </>
  );
};
